import { Fragment, useMemo, useState } from "react";
import { useReviewerBreakdown } from "./useReviewerBreakdown";
import { useUserProfiles } from "@/user/useUserProfiles";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { Customer } from "@/customers/useCustomers";

interface ReviewedBreakdownProps {
  customerIds?: string[];
  customers: Customer[];
  dateFrom?: string;
  dateTo?: string;
}

const ReviewedBreakdown = ({ customerIds, customers, dateFrom, dateTo }: ReviewedBreakdownProps) => {
  const {
    rows: reviewerStats,
    isLoading,
    error,
  } = useReviewerBreakdown({
    customerIds,
    dateFrom,
    dateTo,
  });

  const reviewerIds = useMemo(
    () => (reviewerStats || []).map((r) => r.reviewer_id).filter(Boolean) as string[],
    [reviewerStats],
  );
  const { data: profilesMap } = useUserProfiles(reviewerIds);

  const [expandedReviewers, setExpandedReviewers] = useState<Set<string>>(new Set());

  const toggleReviewerRow = (rowKey: string) => {
    setExpandedReviewers((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const getCustomerLabel = (customerId: string | null | undefined) => {
    if (!customerId) return "Unknown customer";
    const match = customers.find((c) => c.id === customerId);
    return match?.label ?? customerId;
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Error loading reviewer breakdown: {error.message}</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Reviewer</TableHead>
          <TableHead>Finalized</TableHead>
          <TableHead>Rejected</TableHead>
          <TableHead>Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(reviewerStats || []).map((stat) => {
          const profile = stat.reviewer_id ? profilesMap?.[stat.reviewer_id] : undefined;
          const name = profile?.display_name || stat.display_name || "Unknown";
          const rowKey = stat.reviewer_id || name;
          return (
            <Fragment key={rowKey}>
              <TableRow
                role="button"
                tabIndex={0}
                aria-expanded={expandedReviewers.has(rowKey)}
                onClick={() => toggleReviewerRow(rowKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleReviewerRow(rowKey);
                  }
                }}
                className="cursor-pointer"
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {profile?.display_color && (
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: profile.display_color,
                        }}
                      />
                    )}
                    <span>{name}</span>
                  </div>
                </TableCell>
                <TableCell>{stat.approved_count ?? 0}</TableCell>
                <TableCell>{stat.rejected_count ?? 0}</TableCell>
                <TableCell>{stat.total_count ?? (stat.approved_count ?? 0) + (stat.rejected_count ?? 0)}</TableCell>
              </TableRow>
              {expandedReviewers.has(rowKey) && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Customer</TableHead>
                          <TableHead>Finalized</TableHead>
                          <TableHead>Rejected</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(stat.customer_breakdown || []).map((c) => (
                          <TableRow key={`${rowKey}-${c.customer_id || "unknown"}`}>
                            <TableCell>{getCustomerLabel(c.customer_id)}</TableCell>
                            <TableCell>{c.approved_count ?? 0}</TableCell>
                            <TableCell>{c.rejected_count ?? 0}</TableCell>
                            <TableCell>{c.total_count ?? (c.approved_count ?? 0) + (c.rejected_count ?? 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ReviewedBreakdown;
